/**
 * `<TrashSection>` — the pinned Trash folder in `<NotebooksTreePane>` (friction-log fix
 * #90, 2026-05-29).
 *
 * Replaces the short-lived `<SoftDeletedNotesPanel>` in Corpus Health (#88 first cut).
 * The UX feedback: "Delete shouldn't live under Settings — it should be another folder
 * in the notebooks pane with a trash icon… When items are again deleted from within this
 * folder it's a hard delete. Also there must be an Empty Trash option via right-click."
 *
 * Pinned at the bottom of the tree (mirrors Inbox at the top). Visually it reads as a
 * folder; functionally it's a virtual container backed by `useSoftDeletedQuery`. Drag-
 * and-drop is intentionally not wired — neither dragging the Trash folder nor dropping
 * into it makes sense (Trash isn't a real notebook, and a moved-into-trash note would
 * just be a delete, which already has a button).
 *
 * Two delete behaviours co-exist:
 *   - `delete_note` (anywhere ELSE in the tree) → SOFT delete → row moves to Trash.
 *   - "Delete permanently" / "Empty Trash" (here, only here) → HARD delete → FK cascades.
 * That's the two-step undo path the editor copy promises.
 */

import { useEffect, useRef, useState } from 'react';
import { Icon } from '@/components';
import { LoadingState, useConfirmModal } from '@/components';
import { useNavigate } from 'react-router-dom';
import {
  useSoftDeletedQuery,
  useSoftDeletedNotebooksQuery,
  useRestoreNoteMutation,
  useRestoreNotebookMutation,
  useHardDeleteNoteMutation,
  useEmptyTrashMutation,
} from '@/hooks';
import { toEditor } from '@/routes';
import type { NoteSummary, SoftDeletedNotebookSummary } from '@/types';

// Local copies of the small RowContextMenu + useContextMenuPosition helpers so this
// component stays decoupled from NotebooksTreePane's internals. They're tiny and the
// duplication is preferable to exporting otherwise-private machinery.
// ---------------------------------------------------------------------------

interface CtxItem {
  label: string;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
}

function ContextMenu({
  position,
  items,
  onClose,
}: {
  position: { x: number; y: number } | null;
  items: CtxItem[];
  onClose: () => void;
}): JSX.Element | null {
  const ref = useRef<HTMLDivElement>(null);

  // Auto-close on click-outside, Esc, or any scroll (UX feedback #3, 2026-05-29):
  // the prior local copy of this component omitted these listeners, so the menu
  // persisted until the user picked an item. NotebooksTreePane's <RowContextMenu>
  // has the same pattern — we mirror it here rather than re-export the original.
  useEffect(() => {
    if (position === null) return;
    function onMouseDown(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    function onScroll(): void {
      onClose();
    }
    window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('keydown', onKey);
    // Capture phase so scroll on any nested container also closes the menu.
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [position, onClose]);

  if (position === null) return null;
  const MENU_W = 200;
  const MENU_H = 36 * items.length + 16;
  const x =
    position.x + MENU_W > window.innerWidth - 8
      ? Math.max(8, position.x - MENU_W)
      : position.x;
  const y =
    position.y + MENU_H > window.innerHeight - 8
      ? Math.max(8, position.y - MENU_H)
      : position.y;
  return (
    <div
      ref={ref}
      role="menu"
      className="notebooks-pane__ctx-menu"
      style={{ position: 'fixed', left: x, top: y, minWidth: MENU_W }}
    >
      {items.map((item, i) => (
        <button
          key={i}
          type="button"
          role="menuitem"
          disabled={item.disabled}
          onClick={(e) => {
            e.stopPropagation();
            onClose();
            item.onClick();
          }}
          className={
            'notebooks-pane__ctx-item' +
            (item.danger ? ' notebooks-pane__ctx-item--danger' : '')
          }
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

function useCtx(): {
  position: { x: number; y: number } | null;
  open: (e: React.MouseEvent) => void;
  close: () => void;
} {
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  return {
    position,
    open: (e) => {
      e.preventDefault();
      e.stopPropagation();
      setPosition({ x: e.clientX, y: e.clientY });
    },
    close: () => setPosition(null),
  };
}

// ---------------------------------------------------------------------------
// One row inside the open Trash folder.
// ---------------------------------------------------------------------------

interface TrashedRowProps {
  note: NoteSummary;
  onRestore: (note: NoteSummary) => void;
  onHardDelete: (note: NoteSummary) => void;
  isBusy: boolean;
}

function TrashedNoteRow({
  note,
  onRestore,
  onHardDelete,
  isBusy,
}: TrashedRowProps): JSX.Element {
  const navigate = useNavigate();
  const ctx = useCtx();
  return (
    <li>
      <div
        className="notebooks-pane__row notebooks-pane__row--trash"
        tabIndex={0}
        role="treeitem"
        aria-disabled
        onContextMenu={ctx.open}
        onDoubleClick={() => {
          // Quick read-only navigation to the note's body — same as the editor's
          // open-on-click. Useful when the user wants to confirm what they're
          // about to permanently delete.
          navigate(toEditor(note.noteId));
        }}
        style={{ opacity: 0.7 }}
        title="Right-click for Restore / Delete Permanently"
      >
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {note.title || '(untitled)'}
        </span>
      </div>
      <ContextMenu
        position={ctx.position}
        items={[
          {
            label: 'Restore',
            onClick: () => onRestore(note),
            disabled: isBusy,
          },
          {
            label: 'Delete Permanently',
            onClick: () => onHardDelete(note),
            danger: true,
            disabled: isBusy,
          },
        ]}
        onClose={ctx.close}
      />
    </li>
  );
}

// ---------------------------------------------------------------------------
// One row inside the Trash for a SOFT-DELETED NOTEBOOK (#95, 2026-05-29).
// Restore brings it back with its notes intact (the soft-delete preserved their
// notebook_id). Permanent delete is intentionally NOT exposed here yet — Chris's
// call-out: "Hard-delete-only on notebooks would be especially scary." Restore-only
// for the first pass; hard-delete UI is a follow-up.
// ---------------------------------------------------------------------------

interface TrashedNotebookRowProps {
  notebook: SoftDeletedNotebookSummary;
  onRestore: (notebook: SoftDeletedNotebookSummary) => void;
  isBusy: boolean;
}

function TrashedNotebookRow({
  notebook,
  onRestore,
  isBusy,
}: TrashedNotebookRowProps): JSX.Element {
  const ctx = useCtx();
  return (
    <li>
      <div
        className="notebooks-pane__row notebooks-pane__row--trash"
        tabIndex={0}
        role="treeitem"
        aria-disabled
        onContextMenu={ctx.open}
        style={{ opacity: 0.7 }}
        title={
          notebook.noteCountInside > 0
            ? `Right-click to restore — ${notebook.noteCountInside} note${notebook.noteCountInside === 1 ? '' : 's'} will return`
            : 'Right-click to restore'
        }
      >
        <Icon name="folder" size={14} />
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {notebook.title || '(untitled)'}
        </span>
        {notebook.noteCountInside > 0 && (
          <span
            className="notebooks-pane__count"
            aria-label={`${notebook.noteCountInside} notes still tagged with this notebook`}
          >
            {notebook.noteCountInside}
          </span>
        )}
      </div>
      <ContextMenu
        position={ctx.position}
        items={[
          {
            label: 'Restore notebook',
            onClick: () => onRestore(notebook),
            disabled: isBusy,
          },
        ]}
        onClose={ctx.close}
      />
    </li>
  );
}

// ---------------------------------------------------------------------------
// The pinned Trash folder itself.
// ---------------------------------------------------------------------------

export function TrashSection(): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  // Both queries are gated on `expanded` so a collapsed Trash folder doesn't pay for
  // the round-trips. They invalidate cleanly via SSE (#92) when MCP or another tab
  // triggers a soft-delete / restore.
  const trashedNotesQuery = useSoftDeletedQuery(0, { enabled: expanded });
  const trashedNotebooksQuery = useSoftDeletedNotebooksQuery({ enabled: expanded });
  const restoreMutation = useRestoreNoteMutation();
  const restoreNotebookMutation = useRestoreNotebookMutation();
  const hardDeleteMutation = useHardDeleteNoteMutation();
  const emptyTrashMutation = useEmptyTrashMutation();
  const folderCtx = useCtx();
  const { modal: confirmModal, confirm } = useConfirmModal();

  const items = trashedNotesQuery.data?.items ?? [];
  const notebookItems = trashedNotebooksQuery.data?.items ?? [];
  const noteCount = items.length;
  const notebookCount = notebookItems.length;
  const totalCount = noteCount + notebookCount;
  // Empty Trash currently empties NOTES only — notebook hard-delete is deferred (#95
  // follow-up) per "Hard-delete-only on notebooks would be especially scary." The
  // folder context-menu label reflects this so a user doesn't expect their notebooks
  // to be purged.
  const count = noteCount;
  const isBusy =
    restoreMutation.isPending ||
    restoreNotebookMutation.isPending ||
    hardDeleteMutation.isPending ||
    emptyTrashMutation.isPending;

  async function onHardDelete(note: NoteSummary): Promise<void> {
    const ok = await confirm({
      eyebrow: 'Permanent delete',
      title: `Delete ${note.title || '(untitled)'} permanently?`,
      message:
        'This permanently removes the note, its body, its companion record, its tags, ' +
        'and any edges connecting it. This cannot be undone.',
      confirmLabel: 'Delete permanently',
      destructive: true,
    });
    if (!ok) return;
    hardDeleteMutation.mutate(note.noteId);
  }

  async function onEmptyTrash(): Promise<void> {
    if (count === 0) return;
    const ok = await confirm({
      eyebrow: 'Empty trash',
      title: `Permanently delete ${count} note${count === 1 ? '' : 's'}?`,
      message:
        'Every note currently in Trash will be permanently removed along with its body, ' +
        'tags, companion record, and any edges. This cannot be undone.',
      confirmLabel: 'Empty Trash',
      destructive: true,
    });
    if (!ok) return;
    emptyTrashMutation.mutate();
  }

  function onRestore(note: NoteSummary): void {
    restoreMutation.mutate(note.noteId);
  }

  function onRestoreNotebook(notebook: SoftDeletedNotebookSummary): void {
    restoreNotebookMutation.mutate(notebook.notebookId);
  }

  return (
    <div className="notebooks-pane__trash">
      {confirmModal}
      <div className="sb-pin-head">Trash</div>
      <ul className="notebooks-pane__list">
        <li>
          <div
            className="notebooks-pane__row notebooks-pane__row--folder"
            tabIndex={0}
            role="treeitem"
            aria-expanded={expanded}
            onClick={() => setExpanded((e) => !e)}
            onContextMenu={folderCtx.open}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setExpanded((v) => !v);
              }
            }}
            title="Right-click to empty the trash"
          >
            <Icon name={expanded ? 'chevron_down' : 'chevron_right'} size={12} />
            <Icon name="trash" size={14} />
            <span style={{ flex: 1 }}>Trash</span>
            {totalCount > 0 && (
              <span
                className="notebooks-pane__count"
                aria-label={`${totalCount} items in trash`}
              >
                {totalCount}
              </span>
            )}
          </div>
          <ContextMenu
            position={folderCtx.position}
            items={[
              {
                label:
                  count === 0
                    ? 'Empty Trash (no notes to delete)'
                    : `Empty Trash (notes only — ${count})`,
                onClick: () => void onEmptyTrash(),
                danger: true,
                disabled: count === 0 || isBusy,
              },
            ]}
            onClose={folderCtx.close}
          />
          {expanded && (
            <ul className="notebooks-pane__list" style={{ paddingLeft: 16 }}>
              {(trashedNotesQuery.isLoading || trashedNotebooksQuery.isLoading) && (
                <li>
                  <LoadingState label="Loading trash…" />
                </li>
              )}
              {totalCount === 0 &&
                trashedNotesQuery.data !== undefined &&
                trashedNotebooksQuery.data !== undefined && (
                  <li
                    className="notebooks-pane__row notebooks-pane__row--empty"
                    style={{ opacity: 0.6, fontStyle: 'italic' }}
                  >
                    Trash is empty
                  </li>
                )}
              {notebookCount > 0 && (
                <li
                  className="sb-pin-head"
                  style={{ opacity: 0.7, marginTop: 4 }}
                  aria-label="Soft-deleted notebooks"
                >
                  Notebooks ({notebookCount})
                </li>
              )}
              {notebookItems.map((nb) => (
                <TrashedNotebookRow
                  key={nb.notebookId}
                  notebook={nb}
                  onRestore={onRestoreNotebook}
                  isBusy={isBusy}
                />
              ))}
              {noteCount > 0 && (
                <li
                  className="sb-pin-head"
                  style={{ opacity: 0.7, marginTop: 4 }}
                  aria-label="Soft-deleted notes"
                >
                  Notes ({noteCount})
                </li>
              )}
              {items.map((note) => (
                <TrashedNoteRow
                  key={note.noteId}
                  note={note}
                  onRestore={onRestore}
                  onHardDelete={(n) => void onHardDelete(n)}
                  isBusy={isBusy}
                />
              ))}
            </ul>
          )}
        </li>
      </ul>
    </div>
  );
}

